import { RiAddLine, RiLogoutBoxRLine, RiRefreshLine } from 'react-icons/ri';
import type { JSX } from 'react';
import { ToastContainer } from '@/components/features';
import { ConfirmModal } from '@/components/ui';
import { useBrandOffersAdminAuth } from '@/contexts/BrandOffersAdminAuthContext';
import { useBrandOffersPage } from '@/hooks/useBrandOffersPage';
import BrandOfferForm from './BrandOfferForm';
import {
  BRAND_OFFERS_PAGE_COPY,
  OFFER_DESK_HEADING_ID,
} from './BrandOffersPage.constants';
import styles from './BrandOffersPage.module.css';
import OfferFiltersPanel from './OfferFiltersPanel';
import OffersTable from './OffersTable';

const BrandOffersPage = (): JSX.Element => {
  const { displayUser } = useBrandOffersAdminAuth();
  const {
    offers,
    totalCount,
    brands,
    isLoading,
    error,
    selectedIds,
    filters,
    page,
    isFormOpen,
    editingOffer,
    duplicateSource,
    deleteConfirmId,
    isDeleting,
    isBulkActing,
    togglingId,
    totalPages,
    pageSize,
    hasPrevious,
    hasNext,
    brandMap,
    conflictingBrands,
    pageActiveCount,
    allSelected,
    setPage,
    setDeleteConfirmId,
    toggleSelect,
    loadOffers,
    handleFilterChange,
    handleLogout,
    handleToggleActive,
    handleDelete,
    handleBulkActivate,
    handleBulkDeactivate,
    handleBulkDelete,
    handleFormSaved,
    openEdit,
    openNew,
    openDuplicate,
    closeFormDrawer,
    handleSelectAll,
  } = useBrandOffersPage();

  return (
    <div className={styles.container}>
      <div className={styles.shell}>
        <header className={styles.topChrome}>
          <div className={styles.brandBlock}>
            <span className={styles.brandWord}>
              {BRAND_OFFERS_PAGE_COPY.brandWord}
            </span>
            <span aria-hidden className={styles.brandPipe} />
            <span className={styles.brandModule}>
              {BRAND_OFFERS_PAGE_COPY.brandModule}
            </span>
          </div>
          <div className={styles.topChromeActions}>
            {displayUser && (
              <span
                className={styles.sessionUser}
                title={BRAND_OFFERS_PAGE_COPY.sessionUserTitle}
              >
                {displayUser}
              </span>
            )}
            <button
              type="button"
              className={styles.ghostBtn}
              title={BRAND_OFFERS_PAGE_COPY.logoutTitle}
              onClick={handleLogout}
            >
              <RiLogoutBoxRLine size={16} />
              {BRAND_OFFERS_PAGE_COPY.leave}
            </button>
            <button
              type="button"
              className={styles.iconOnlyBtn}
              disabled={isLoading}
              title={BRAND_OFFERS_PAGE_COPY.reloadTitle}
              onClick={loadOffers}
            >
              <RiRefreshLine size={18} />
            </button>
            <button
              type="button"
              className={styles.primaryBtn}
              onClick={openNew}
            >
              <RiAddLine size={16} />
              {BRAND_OFFERS_PAGE_COPY.newOffer}
            </button>
          </div>
        </header>

        <section
          className={styles.hero}
          aria-labelledby={OFFER_DESK_HEADING_ID}
        >
          <div className={styles.heroCopy}>
            <p className={styles.heroEyebrow}>
              {BRAND_OFFERS_PAGE_COPY.heroEyebrow}
            </p>
            <h1 id={OFFER_DESK_HEADING_ID} className={styles.heroTitle}>
              {BRAND_OFFERS_PAGE_COPY.heroTitleBefore}
              <span className={styles.heroAccent}>
                {BRAND_OFFERS_PAGE_COPY.heroTitleAccent}
              </span>
              {BRAND_OFFERS_PAGE_COPY.heroTitleAfter}
            </h1>
            <p className={styles.heroLead}>{BRAND_OFFERS_PAGE_COPY.heroLead}</p>
          </div>
          <div className={styles.statGrid} role="status">
            <div className={styles.statCard}>
              <span className={styles.statValue}>{totalCount}</span>
              <span className={styles.statLabel}>
                {BRAND_OFFERS_PAGE_COPY.statTotalLabel}
              </span>
            </div>
            <div className={styles.statCard}>
              <span className={styles.statValue}>{offers.length}</span>
              <span className={styles.statLabel}>
                {BRAND_OFFERS_PAGE_COPY.statPageLabel}
              </span>
            </div>
            <div className={styles.statCard}>
              <span className={styles.statValue}>{pageActiveCount}</span>
              <span className={styles.statLabel}>
                {BRAND_OFFERS_PAGE_COPY.statLiveLabel}
              </span>
            </div>
          </div>
        </section>

        <div className={styles.workspace}>
          <OfferFiltersPanel
            filters={filters}
            brands={brands}
            onChange={handleFilterChange}
          />
          <div className={styles.workspaceMain}>
            <OffersTable
              offers={offers}
              isLoading={isLoading}
              error={error}
              totalCount={totalCount}
              page={page}
              totalPages={totalPages}
              pageSize={pageSize}
              hasPrevious={hasPrevious}
              hasNext={hasNext}
              brandMap={brandMap}
              selectedIds={selectedIds}
              allSelected={allSelected}
              togglingId={togglingId}
              isBulkActing={isBulkActing}
              conflictingBrands={conflictingBrands}
              onSelectAll={handleSelectAll}
              onToggleSelect={toggleSelect}
              onToggleActive={handleToggleActive}
              onEdit={openEdit}
              onDuplicate={openDuplicate}
              onDeleteRequest={setDeleteConfirmId}
              onPageChange={setPage}
              onOpenNew={openNew}
              onBulkActivate={handleBulkActivate}
              onBulkDeactivate={handleBulkDeactivate}
              onBulkDelete={handleBulkDelete}
            />
          </div>
        </div>
      </div>

      {deleteConfirmId !== null && (
        <ConfirmModal
          eyebrow={BRAND_OFFERS_PAGE_COPY.deleteModalEyebrow}
          body={
            <>
              {BRAND_OFFERS_PAGE_COPY.deleteModalBeforeStrong}
              <strong>#{deleteConfirmId}</strong>
              {BRAND_OFFERS_PAGE_COPY.deleteModalAfterStrong}
            </>
          }
          cancelLabel={BRAND_OFFERS_PAGE_COPY.deleteModalCancel}
          confirmLabel={
            isDeleting
              ? BRAND_OFFERS_PAGE_COPY.deleteModalConfirmLoading
              : BRAND_OFFERS_PAGE_COPY.deleteModalConfirm
          }
          isLoading={isDeleting}
          onCancel={() => setDeleteConfirmId(null)}
          onConfirm={() => handleDelete(deleteConfirmId)}
        />
      )}

      {isFormOpen && (
        <BrandOfferForm
          offer={editingOffer}
          duplicateSource={duplicateSource ?? undefined}
          onClose={closeFormDrawer}
          onSaved={handleFormSaved}
        />
      )}
      <ToastContainer />
    </div>
  );
};

export default BrandOffersPage;
